import { daymapTheme, AuthContextProvider, SchoolContextProvider, AppContextProvider, useApp, PushNotificationContextProvider, usePushNotifications } from 'daymap-react'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { createBrowserRouter, createHashRouter, Link, LinkProps, Outlet, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import {ErrorBoundary} from './pages/error' 
import { Capacitor } from '@capacitor/core';
import { FileViewer } from '@capacitor/file-viewer';
import 'dayjs/locale/en-au'

import MainLayout from './layouts/main'
import StudentLayout from './layouts/student'
import Home from './pages/home'
import Messaging from './pages/messaging'
import Calendar from './pages/calendar'
import NotFound from './pages/notfound'
import Timetable from './pages/student/calendar'
import Attendance from './pages/student/attendance'
import Notifications from './pages/student/notifications';
import Points from './pages/student/points';
import Assessment from './pages/student/Assessment';
import Records from './pages/student/Records';
import Reports from './pages/student/reports';
import Interviews from './pages/interviews'

import '@fontsource/inter/100.css';
import '@fontsource/inter/200.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import { forwardRef, useEffect } from 'react';
import Record from './pages/student/Records/[formid]';
import Message, { NewMessage } from './pages/message';
import EventView from './pages/student/calendar/[eventid]';
import AssessmentTask from './pages/student/Assessment/[taskid]';
import { PasswordReset } from './pages/[anon] password';
import { PasswordResetRequest } from './pages/[anon] password/requestreset';
import Payments from './pages/payments';
import { ExcursionView } from './pages/student/calendar/excursion';
import { DaymapThemeProvider } from './context/theme';
import Attachments from './pages/student/attachments';
import { Schedule } from './pages/student/schedule';
import { Notes } from './pages/student/notes';
import MyDetails from './pages/mydetails';
import Annotations from './pages/student/Assessment/annotations';
import { Classes } from './pages/student/Classes';
import { ClassDetail } from './pages/student/Classes/[classId]';

const createRouter = Capacitor.isNativePlatform() ? createHashRouter : createBrowserRouter;

const ContextRoutes = () => {
  let {token, deviceType, tokenError} = usePushNotifications();

  return <SchoolContextProvider>
     <AuthContextProvider device={{token, deviceType, tokenError}} clientId="Connect.API">
      <AppContextProvider>
          <FileDownloadHandler />
          <DaymapThemeProvider LinkBehavior={LinkBehavior}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
              <Outlet />
            </LocalizationProvider>
          </DaymapThemeProvider>
      </AppContextProvider>
     </AuthContextProvider>
  </SchoolContextProvider>
};

const FileDownloadHandler = () => {
  const { setNativeDownload } = useApp();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setNativeDownload(nativeDownload);
    }
  }, []);

  const nativeDownload = async (url: string) => {
    await FileViewer.openDocumentFromUrl({
      url
    })
  }

  return null;
}

const router = createRouter([
  {
    path: "/password/requestreset",
    element: <PasswordResetRequest/>
  },
  {
    path: "/passwordreset",
    element: <PasswordReset/>
  },
  {
    path: '',
    element:<ContextRoutes />,
    children: [
    {
      path: '',
      element: <MainLayout />,
      errorElement: <ErrorBoundary />,
      children: [
        { path: '/', element: <Home /> },
        { path: '/messaging', element: <Messaging />},
        { path: '/message/new/:messageType', element: <NewMessage/>},
        { path: '/message/:messageId', element: <Message/>},
        { path: '/calendar', element: <Calendar />},
        { path: '/payments', element: <Payments />},
        { path: '/student', element: <StudentLayout />, children: [
          { path: '/student/:studentId', element: <Notifications /> },
          { path: '/student/:studentId/timetable', element: <Timetable /> },
          { path: '/student/:studentId/timetable/:eventId', element: <EventView /> },
          { path: '/student/:studentId/attendance', element: <Attendance /> },
          { path: '/student/:studentId/attachments', element: <Attachments /> },
          { path: '/student/:studentId/assessment', element: <Assessment /> },
          { path: '/student/:studentId/assessment/:taskid', element: <AssessmentTask /> },
          { path: '/student/:studentId/assessment/:taskid/annotations/:fileId', element: <Annotations />},
          { path: '/student/:studentId/points', element: <Points /> },
          { path: '/student/:studentId/records', element: <Records /> },
          { path: '/student/:studentId/records/:formId', element: <Record /> },
          { path: '/student/:studentId/reports', element: <Reports /> },
          { path: '/student/:studentId/excursion/:eventId', element: <ExcursionView /> },
          { path: '/student/:studentId/schedule', element: <Schedule /> },
          { path: '/student/:studentId/notes', element: <Notes /> },
          { path: '/student/:studentId/classes/:classId/:year', element: <ClassDetail /> },
          { path: '/student/:studentId/classes/:classId', element: <ClassDetail /> },
          { path: '/student/:studentId/classes', element: <Classes /> },
        ]},
        { path: '/interviews/:groupId', element: <Interviews />},
        { path: '/mydetails', element: <MyDetails />},
        { path: '*', element: <NotFound /> },
      ],
    }]
  }
],  { basename: Capacitor.isNativePlatform() ? '/' : '/portal' })

const LinkBehavior = forwardRef<
    HTMLAnchorElement,
    Omit<LinkProps, "to"> & { href: LinkProps["to"] }
>((props, ref) => {
    const {href, ...other} = props;
    return <Link ref={ref} to={href} {...other} />;
});

const theme = daymapTheme({ LinkBehavior });

// Defaults for react query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // Dont retry in the event of an error
      refetchOnWindowFocus: false, // Dont rerun queries if they come back from another tab
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}> {/* This is used to style the anonymous routes there is an inner provider for the daymap theme*/}
        <PushNotificationContextProvider>
          <RouterProvider router={router}  />
        </PushNotificationContextProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
